import React, { useState, useCallback } from 'react';
import { Mail, ArrowLeft, X } from 'lucide-react';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { motion } from 'framer-motion';
import { useOtpCooldown, usePointerActivation } from '@/hooks';

interface VerifyOtpContentProps {
	email: string;
	onVerify: (code: string) => Promise<void>;
	onResendOtp: () => Promise<void>;
	onBack: () => void;
	onClose?: () => void;
	isLoading?: boolean;
	error?: string;
	isPopUp?: boolean;
}

export const VerifyOtpContent: React.FC<VerifyOtpContentProps> = ({
	email,
	onVerify,
	onResendOtp,
	onBack,
	onClose,
	isLoading = false,
	error,
	isPopUp,
}) => {
	const [otp, setOtp] = useState('');

	const { timeLeft, startCooldown, isCooldownActive } = useOtpCooldown(email);

	const handleClose = useCallback(() => {
		if (onClose) onClose();
	}, [onClose]);
	const closePointerProps = usePointerActivation(handleClose);

	const handleBackClick = useCallback(() => {
		if (onBack) onBack();
	}, [onBack]);
	const backPointerProps = usePointerActivation(handleBackClick);

	const handleComplete = (value: string) => {
		onVerify(value);
	};

	const handleResend = async () => {
		if (isCooldownActive || isLoading) return;

		await onResendOtp();

		startCooldown();
	};

	return (
		<div className="flex flex-col items-center w-full h-full relative overflow-hidden">
			<div className="absolute top-0 left-0 right-0 flex justify-between items-center w-full z-10">
				<button
					{...backPointerProps}
					className="flex items-center justify-center w-10 h-10 rounded-full bg-[#1E2748]/50 hover:bg-[#1E2748] transition-colors"
				>
					<ArrowLeft className="w-5 h-5 text-[#F7F7F8]" />
				</button>
				{onClose && (
					<button
						{...closePointerProps}
						className="flex items-center justify-center w-10 h-10 rounded-full bg-[#1E2748]/50 hover:bg-[#1E2748] transition-colors"
					>
						<X className="w-5 h-5 text-[#F7F7F8]" />
					</button>
				)}
			</div>

			<div className="mt-12 mb-6 [@media(max-height:600px)]:mt-8 [@media(max-height:600px)]:mb-3">
				<div className="flex items-center justify-center w-[56px] h-[56px] rounded-full bg-[#1E2748]">
					<Mail className="w-8 h-8 text-[#9FA8C6]" strokeWidth={1} />
				</div>
			</div>

			<div className="text-center mb-10 [@media(max-height:600px)]:mb-5">
				<h2 className="text-[20px] font-semibold text-[#F7F7F8] mb-4 [@media(max-height:600px)]:mb-2">
					Input Confirmation Code
				</h2>
				<div className="text-[14px] text-[#B5B6BA] leading-relaxed">
					<p>
						Please check <span className="font-medium text-[#F7F7F8]">{email}</span> for an
					</p>
					<p>
						email from <span className="text-[#F7F7F8]">sportspredict.com</span> and enter
					</p>
					<p>your code below.</p>
				</div>
			</div>

			{error && (
				<motion.div
					initial={{ opacity: 0, y: -10 }}
					animate={{ opacity: 1, y: 0 }}
					className="mb-6 px-4 py-2 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm text-center"
				>
					{error}
				</motion.div>
			)}

			<div className="mb-16 [@media(max-height:600px)]:mb-8">
				<InputOTP
					maxLength={6}
					value={otp}
					onChange={setOtp}
					onComplete={handleComplete}
					disabled={isLoading}
					autoFocus
				>
					<InputOTPGroup>
						{[...Array(6)].map((_, i) => (
							<InputOTPSlot key={i} index={i} />
						))}
					</InputOTPGroup>
				</InputOTP>
			</div>

			<div className="flex items-center gap-2 text-[12px]">
				<span className="text-[#B5B6BA]">Didn’t get an email?</span>
				<button
					onClick={handleResend}
					disabled={isLoading || timeLeft > 0}
					className="font-medium text-[#81A1F8] hover:underline disabled:opacity-50 disabled:no-underline cursor-pointer disabled:cursor-not-allowed"
				>
					{timeLeft > 0 ? `Resend code (${timeLeft}s)` : 'Resend code'}
				</button>
			</div>
		</div>
	);
};
